<script lang="ts" setup>
import { ref } from "vue";
import {
  ShortDocumentDocument,
  type ShortDocumentFragment as ShortDocument,
} from "@/graphql/operations";
import { DocumentSearch, DocumentPagePicker, Modal } from "@/components";
import graphqlClient from "@/graphql/client";
import { MimeType } from "@/util";

const props = defineProps({ pending: { type: Boolean, default: false } });

const showDocumentSearch = ref(true);
const showDocumentPagePicker = ref(false);
const pageIds = ref<Array<string>>([]);

const emits = defineEmits(["close", "submit", "addVideo"]);

const close = () => {
  emits("close");
};

const selectedDocument = ref<ShortDocument>();
const selectionFailed = ref(false);
let selectionRequestId = 0;

const setSelectedDocument = async (values: Map<string, string>) => {
  const id = Array.from(values.keys())[0];
  const requestId = ++selectionRequestId;
  selectedDocument.value = undefined;
  selectionFailed.value = false;

  if (!id) {
    return;
  }

  const res = await graphqlClient
    .query(ShortDocumentDocument, { id: id })
    .toPromise();

  if (requestId !== selectionRequestId) {
    return;
  }

  if (res.error || !res.data?.document) {
    selectionFailed.value = true;
    return;
  }

  selectedDocument.value = res.data.document;
};

const setSelectedPages = (pages: Array<string>) => {
  pageIds.value = pages;
};

const nextStep = () => {
  if (!selectedDocument.value || props.pending) {
    return;
  }

  if (MimeType.isVideo(selectedDocument.value.mimeType)) {
    emits("addVideo", selectedDocument.value.id);
  } else {
    showDocumentSearch.value = false;
    showDocumentPagePicker.value = true;
  }
};

const previousStep = () => {
  pageIds.value = [];
  showDocumentSearch.value = true;
  showDocumentPagePicker.value = false;
};

const submitPages = () => {
  if (!props.pending) emits("submit", [...pageIds.value]);
};
</script>

<template>
  <Modal
    :active="true"
    :close-disabled="pending"
    :scrolling="true"
    @close="close"
    class="huge"
    data-test="document-pages-picker"
  >
    <template v-slot:header>
      {{
        showDocumentSearch
          ? $t("documentPagePicker.header.document")
          : $t("documentPagePicker.header.pages")
      }}
    </template>

    <template v-slot:body>
      <div v-if="selectionFailed" class="message error" role="alert">
        {{ $t("documentPagePicker.selectionFailed") }}
      </div>
      <DocumentSearch
        inherit-classic-theme
        v-if="showDocumentSearch"
        :single-select="true"
        :selected-document="selectedDocument?.id"
        @change="setSelectedDocument"
        :mustSupportFlows="true"
      />

      <div v-else-if="showDocumentPagePicker">
        <DocumentPagePicker
          :document-id="selectedDocument!.id"
          @change="setSelectedPages"
        />
      </div>
    </template>

    <template v-slot:footer>
      <div v-if="showDocumentSearch" class="buttons">
        <button class="button cancel" :disabled="pending" @click="close">
          {{ $t("documentPagePicker.footer.close") }}
        </button>

        <button
          class="button submit"
          :disabled="!selectedDocument || pending"
          @click="nextStep"
          data-test="select-document-submit"
        >
          {{ $t("documentPagePicker.footer.selectDocument") }}
        </button>
      </div>

      <div
        v-else-if="showDocumentPagePicker && selectedDocument"
        class="buttons"
      >
        <button class="button cancel" :disabled="pending" @click="previousStep">
          {{ $t("documentPagePicker.footer.goBack") }}
        </button>

        <button
          class="button submit"
          @click="submitPages"
          :disabled="pageIds.length === 0 || pending"
          data-test="select-pages-submit"
        >
          {{ $t("documentPagePicker.footer.selectPages") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.message {
  box-sizing: border-box;
  max-width: 100%;
}

.footer_buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
}

.button.submit {
  background-color: var(--classic-accent, #35b6ba);
  color: var(--classic-on-accent, white);
}
</style>
